/**
 * Notification activity log for installer Notifications Center.
 * Uses RPC get_notification_activity (org-scoped).
 */
import { useQuery } from '@tanstack/react-query';
import { useApp } from '@/contexts/AppContext';
import { supabase } from '@/integrations/supabase/client';

const QUERY_KEY = 'notification-activity';

export interface NotificationActivityRow {
  id: string;
  property_id: string;
  property_name: string;
  event_kind: string;
  severity: string;
  title: string;
  outcome: string;
  suppression_reason: string | null;
  audience_type: string;
  created_at: string;
}

export interface NotificationActivityFilters {
  dateFrom?: string;
  dateTo?: string;
  propertyId?: string;
  eventKind?: string;
  outcome?: string;
  audienceType?: string | null;
  limit?: number;
}

async function fetchActivity(
  orgId: string | null,
  filters: NotificationActivityFilters
): Promise<NotificationActivityRow[]> {
  if (!orgId) return [];
  const { data, error } = await supabase.rpc('get_notification_activity', {
    p_org_id: orgId,
    p_date_from: filters.dateFrom ?? null,
    p_date_to: filters.dateTo ?? null,
    p_property_id: filters.propertyId ?? null,
    p_event_kind: filters.eventKind ?? null,
    p_outcome: filters.outcome ?? null,
    p_audience_type: filters.audienceType ?? null,
    p_limit: filters.limit ?? 100,
  });
  if (error) throw error;
  return (data ?? []) as NotificationActivityRow[];
}

export function useNotificationActivity(filters: NotificationActivityFilters = {}) {
  const { organization } = useApp();
  const orgId = organization?.id ?? null;
  const query = useQuery({
    queryKey: [QUERY_KEY, orgId, filters],
    queryFn: () => fetchActivity(orgId, filters),
    enabled: Boolean(orgId),
  });
  return {
    activity: query.data ?? [],
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
}
